import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, ElementRef, HostListener, inject, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { UserService } from '../../shared/services/user.service';
import { PREFERENCE_CATEGORIES } from '../../shared/services/preferences.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Modal } from '../../components/ui/modal/modal';
import { ButtonComponent } from '../../components/ui/button/button';
import { AuthStateService } from '../../shared/states/auth.state';
import { FriendRequestService, FriendshipStatus } from '../../shared/services/friend-request.service';
import { RelationshipService } from '../../shared/services/relationship.service';
import { Post } from '../../pages/posts/posts';
import { CameraCaptureComponent, CapturedPhoto } from '../../components/ui/camera-capture/camera-capture';
import { ChatService } from '../../shared/services/chat.service';
import { FriendRequestLimitPopup } from '../../components/ui/friend-request-limit-popup/friend-request-limit-popup';
import { ConfirmationPopup } from '../../components/ui/confirmation-popup/confirmation-popup';
import { BlockUserConfirm } from '../../components/ui/block-user-confirm/block-user-confirm';
import { DropdownComponent } from '../../components/ui/dropdown/dropdown';
import { DropdownItemComponent } from '../../components/ui/dropdown/dropdown-item/dropdown-item';
import { isHeicFile, readAsPreviewDataUrl } from '../../shared/utils/heic.util';
// ── File size limits ──────────────────────────────────────────
const MAX_PROFILE_FILE_SIZE_MB = 20;
const MAX_COVER_FILE_SIZE_MB   = 20;

const PROFILE_CROP_PX = 192;
const COVER_CROP_W    = 338;
const COVER_CROP_H    = 160;
const PROFILE_PREVIEW = 80;
const COVER_PREV_W    = 169;
const COVER_PREV_H    = 80;

@Component({
  selector: 'app-profile-sidebar',
  imports: [CommonModule, FormsModule, Modal, ButtonComponent, CameraCaptureComponent, FriendRequestLimitPopup, ConfirmationPopup, BlockUserConfirm, DropdownComponent, DropdownItemComponent],
  templateUrl: './profile-sidebar.html'
})
export class ProfileSidebar {

  isUploading      = false;
  isCoverUploading = false;

  readonly stats = [
    { value: 248, label: 'Followers' },
    { value: 248, label: 'Following' },
    { value: 248, label: 'Reactions' },
    { value: 248, label: 'Comments' }
  ];

  userInfo: any;
  profileUnavailable = false;

  // ── Modal flags ───────────────────────────────────────────
  isProfileImageOpen = false;
  isCoverImageOpen   = false;

  // ── Message modal state ───────────────────────────────────
  isMessageModalOpen  = false;
  messageText         = '';
  isSendingMessage    = false;
  messageSendError    = '';

  // ── Camera (profile image only) ───────────────────────────
  isCameraOpen = false;

  openProfileCamera(): void  { this.isCameraOpen = true; }
  closeProfileCamera(): void { this.isCameraOpen = false; }

  onProfilePhotoCaptured(photo: CapturedPhoto): void {
    this.isCameraOpen = false;
    this.selectedFile     = photo.file;
    this.selectedImageUrl = photo.dataUrl;
    this.offsetX = 0; this.offsetY = 0; this.zoomLevel = 1;
    setTimeout(() => this.fitImageToCropCircle(), 50);
    this.cdr.markForCheck();
  }

  // ── Profile image state ───────────────────────────────────
  selectedImageUrl: string | null = null;
  selectedFile: File | null       = null;
  fileError   = '';
  isDragging  = false;
  zoomLevel   = 1;
  initialScale = 1;
  offsetX     = 0;
  offsetY     = 0;
  private dragging    = false;
  private lastMouseX  = 0;
  private lastMouseY  = 0;

  // ── Cover image state ─────────────────────────────────────
  selectedCoverUrl:  string | null = null;
  selectedCoverFile: File | null   = null;
  coverFileError   = '';
  isCoverDragging  = false;
  coverZoomLevel   = 1;
  coverInitialScale = 1;
  coverOffsetX     = 0;
  coverOffsetY     = 0;
  private coverDragging   = false;
  private coverLastMouseX = 0;
  private coverLastMouseY = 0;

  // ── Profile preview getters ───────────────────────────────
  get minZoom()         { return this.initialScale; }
  get previewOffsetX()  { return this.offsetX * (PROFILE_PREVIEW / PROFILE_CROP_PX); }
  get previewOffsetY()  { return this.offsetY * (PROFILE_PREVIEW / PROFILE_CROP_PX); }
  get previewImgSize(): string {
    const el = this.cropImage?.nativeElement;
    if (!el?.offsetWidth) return 'auto';
    return (el.offsetWidth * (PROFILE_PREVIEW / PROFILE_CROP_PX)) + 'px';
  }

  // ── Cover preview getters ─────────────────────────────────
  get coverMinZoom()        { return this.coverInitialScale; }
  get coverPreviewOffsetX() { return this.coverOffsetX * (COVER_PREV_W / COVER_CROP_W); }
  get coverPreviewOffsetY() { return this.coverOffsetY * (COVER_PREV_H / COVER_CROP_H); }
  get coverPreviewImgW(): string {
    const el = this.coverCropImage?.nativeElement;
    if (!el?.offsetWidth) return 'auto';
    return (el.offsetWidth * (COVER_PREV_W / COVER_CROP_W)) + 'px';
  }

  @ViewChild('exportCanvas')      exportCanvas!:      ElementRef<HTMLCanvasElement>;
  @ViewChild('cropImage')         cropImage!:         ElementRef<HTMLImageElement>;
  @ViewChild('coverExportCanvas') coverExportCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('coverCropImage')    coverCropImage!:    ElementRef<HTMLImageElement>;

  private relationshipService  = inject(RelationshipService);
  private friendRequestService = inject(FriendRequestService);
  private chatService          = inject(ChatService);
  private destroyRef           = inject(DestroyRef);

  constructor(
    private userService: UserService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private authState: AuthStateService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const userName = params.get('userName');
      this.getProfile(userName);
    });

    // Friend / follow actions taken elsewhere on the page (e.g. the friend icon
    // on one of this user's post cards) used to leave this panel stale until a
    // reload (SAF-841). Changes made from this panel arrive here too but are
    // no-ops, since its own handlers already applied them.
    this.friendRequestService.friendshipChanged$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(change => {
        const f = this.userInfo?.friendship;
        if (!f) return;
        const matches = (change.userId && change.userId === this.userInfo.id)
          || (!!change.requestId && change.requestId === f.requestId);
        if (!matches || f.status === 'BLOCKED') return;
        f.status = change.status;
        if (change.requestId !== undefined) f.requestId = change.status === 'NONE' ? null : change.requestId;
        this.cdr.detectChanges();
      });

    this.relationshipService.followChanged$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ userId, isFollowed }) => {
        if (!this.userInfo || userId !== this.userInfo.id || this.userInfo.isFollowed === isFollowed) return;
        this.userInfo.isFollowed = isFollowed;
        this.userInfo.followers = Math.max(0, (this.userInfo.followers ?? 0) + (isFollowed ? 1 : -1));
        this.cdr.detectChanges();
      });
  }

  getProfile(userName?: any) {
    this.profileUnavailable = false;
    this.userService.getUserProfile(userName).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.userInfo = res.data;
          this.cdr.detectChanges();
        }
      },
      error: (err) => {
        console.error(err);
        if (err?.status === 404) {
          this.userInfo = null;
          this.profileUnavailable = true;
          this.cdr.detectChanges();
        }
      }
    });
  }

  redirectToSetting() { this.router.navigate(['/dashboard/setting']); }

  /** Preference categories (Interested In, Roles, …) the member chose to
   *  show on their profile via "Show on Profile?" on the Preferences screen.
   *  The API already strips hidden ones for other viewers; filtering here
   *  too keeps the owner's own view identical to what others see. */
  get visiblePreferences(): { label: string; values: string[] }[] {
    const prefs = this.userInfo?.preferences ?? {};
    const vis = this.userInfo?.profileVisibility ?? {};
    return PREFERENCE_CATEGORIES
      .filter(c => (this.userInfo?.isMyProfile ? vis[c.key] : true))
      .map(c => ({ label: c.label, values: Array.isArray(prefs[c.key]) ? prefs[c.key] : [] }))
      .filter(c => c.values.length > 0);
  }

  get attractedToDisplay(): string {
    const v = this.userInfo?.attractedTo;
    return Array.isArray(v) ? v.join(', ') : (v ?? '');
  }

  // ══════════════════════════════════════════════════════════
  // MESSAGE MODAL
  // ══════════════════════════════════════════════════════════

  openMessageModal(): void {
    this.isMessageModalOpen = true;
    this.messageText        = '';
    this.messageSendError   = '';
    this.isSendingMessage   = false;
    this.cdr.markForCheck();
  }

  closeMessageModal(): void {
    this.isMessageModalOpen = false;
    this.messageText        = '';
    this.messageSendError   = '';
    this.isSendingMessage   = false;
    this.cdr.markForCheck();
  }

  /**
   * Flow:
   * 1. Call openDm() to create/get the conversation (works whether friends or not —
   *    non-friends get a messageRequest conversation).
   * 2. Send the typed message via REST so we have a confirmed message before navigating.
   * 3. Navigate to /dashboard/chat and let ChatService open the conversation.
   */
  sendFirstMessage(): void {
    const text = this.messageText.trim();
    if (!text || !this.userInfo?.id) return;

    this.isSendingMessage  = true;
    this.messageSendError  = '';
    this.cdr.markForCheck();

    // Step 1: Open or retrieve the DM conversation
    this.chatService.openDm(this.userInfo.id).subscribe({
      next: (res) => {
        if (!res.success) {
          this.messageSendError = 'Could not open conversation. Please try again.';
          this.isSendingMessage = false;
          this.cdr.markForCheck();
          return;
        }

        const conversation = res.data;

        // Step 2: Send the first message via REST (confirmed delivery before navigate)
        this.chatService.sendMessageRest(conversation._id, 'TEXT', text).subscribe({
          next: (msgRes) => {
            this.isSendingMessage = false;

            // Step 3: Navigate to chat, ChatService will open the conversation
            this.closeMessageModal();

            // Ensure chat service is initialised (it guards double-init)
            this.chatService.init();

            // Open the conversation in the service state so the chat page
            // picks it up immediately via activeConversation$
            this.chatService.openConversation(conversation);

            // Navigate to the chat page
            this.router.navigate(['/dashboard/chat']);
            this.cdr.markForCheck();
          },
          error: (err) => {
            this.isSendingMessage = false;
            this.messageSendError = err?.error?.message ?? 'Failed to send message.';
            this.cdr.markForCheck();
          },
        });
      },
      error: (err) => {
        this.isSendingMessage = false;
        this.messageSendError = err?.error?.message ?? 'Failed to open conversation.';
        this.cdr.markForCheck();
      },
    });
  }

  onMessageKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendFirstMessage();
    }
  }

  // ══════════════════════════════════════════════════════════
  // PROFILE IMAGE MODAL
  // ══════════════════════════════════════════════════════════

  openProfileImageModal()  { this.isProfileImageOpen = true; }
  closeProfileImageModal() { this.isProfileImageOpen = false; this.resetImage(); }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) this.handleFile(input.files[0]);
  }

  private static readonly ALLOWED_IMAGE_MIMES = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif'];
  private static readonly ALLOWED_IMAGE_EXT_RE = /\.(jpe?g|jfif|png|heic|heif)$/i;

  /** Some browsers/OSes report an empty or unrecognized mimetype for less
   *  common formats like .jfif (which is really just JPEG data) — fall back
   *  to the file extension in that case instead of rejecting a valid image. */
  private isAllowedImageFile(file: File): boolean {
    if (ProfileSidebar.ALLOWED_IMAGE_MIMES.includes(file.type)) return true;
    if (isHeicFile(file)) return true;
    if (!file.type) return ProfileSidebar.ALLOWED_IMAGE_EXT_RE.test(file.name);
    return false;
  }

  handleFile(file: File) {
    this.fileError = '';
    if (!this.isAllowedImageFile(file)) {
      this.fileError = 'Only JPG, JPEG, JFIF, PNG, and HEIC files are allowed.'; return;
    }
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > MAX_PROFILE_FILE_SIZE_MB + 0.001) {
      this.fileError = `Maximum file size is ${MAX_PROFILE_FILE_SIZE_MB}MB. Your file is ${sizeMB.toFixed(1)}MB.`; return;
    }
    this.selectedFile = file;
    const reader = new FileReader();
    reader.onload = (e) => {
      this.selectedImageUrl = e.target?.result as string;
      this.offsetX = 0; this.offsetY = 0; this.zoomLevel = 1;
      setTimeout(() => this.fitImageToCropCircle(), 50);
      this.cdr.markForCheck();
    };
    reader.onerror = () => {
      this.fileError = `Couldn't read "${file.name}". Please try another image.`;
      this.cdr.markForCheck();
    };
    // HEIC is shown in the cropper via a temporary JPEG; the cropped result
    // is uploaded and stored by the API as WebP.
    readAsPreviewDataUrl(reader, file);
  }

  fitImageToCropCircle() {
    const el = this.cropImage?.nativeElement;
    if (!el?.naturalWidth) return;
    const scaleX = PROFILE_CROP_PX / (el.offsetWidth  || el.naturalWidth);
    const scaleY = PROFILE_CROP_PX / (el.offsetHeight || el.naturalHeight);
    this.initialScale = Math.max(scaleX, scaleY, 1);
    this.zoomLevel    = this.initialScale;
    this.offsetX = 0; this.offsetY = 0;
    this.cdr.markForCheck();
  }

  resetImage() {
    this.selectedImageUrl = null; this.selectedFile = null;
    this.fileError = ''; this.zoomLevel = 1; this.initialScale = 1;
    this.offsetX = 0; this.offsetY = 0;
  }

  onDragOver(e: DragEvent)  { e.preventDefault(); this.isDragging = true; }
  onDragLeave(e: DragEvent) { this.isDragging = false; }
  onDrop(e: DragEvent) {
    e.preventDefault(); this.isDragging = false;
    const file = e.dataTransfer?.files[0]; if (file) this.handleFile(file);
  }

  uploadImage() {
    if (!this.selectedImageUrl || !this.exportCanvas || !this.cropImage) return;

    const canvas = this.exportCanvas.nativeElement;
    const ctx    = canvas.getContext('2d')!;
    const size   = 400;
    canvas.width = canvas.height = size;

    const imgEl    = this.cropImage.nativeElement;
    const renderedW = imgEl.offsetWidth;
    const renderedH = imgEl.offsetHeight;

    const img = new Image();
    img.onload = () => {
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();

      const imgScaleW = (renderedW / PROFILE_CROP_PX) * size * this.zoomLevel;
      const imgScaleH = (renderedH / PROFILE_CROP_PX) * size * this.zoomLevel;

      const drawX = size / 2 + (this.offsetX / PROFILE_CROP_PX) * size - imgScaleW / 2;
      const drawY = size / 2 + (this.offsetY / PROFILE_CROP_PX) * size - imgScaleH / 2;

      ctx.drawImage(img, drawX, drawY, imgScaleW, imgScaleH);
      canvas.toBlob(
        (blob) => { if (blob) this.callUploadApi(blob, 'PROFILE'); },
        'image/jpeg', 0.92
      );
    };
    img.src = this.selectedImageUrl;
  }

  // ══════════════════════════════════════════════════════════
  // COVER IMAGE MODAL
  // ══════════════════════════════════════════════════════════

  openCoverImageModal()  { this.isCoverImageOpen = true; }
  closeCoverImageModal() { this.isCoverImageOpen = false; this.resetCoverImage(); }

  onCoverFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) this.handleCoverFile(input.files[0]);
  }

  handleCoverFile(file: File) {
    this.coverFileError = '';
    if (!this.isAllowedImageFile(file)) {
      this.coverFileError = 'Only JPG, JPEG, JFIF, PNG, and HEIC files are allowed.'; return;
    }
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > MAX_COVER_FILE_SIZE_MB + 0.001) {
      this.coverFileError = `Maximum file size is ${MAX_COVER_FILE_SIZE_MB}MB. Your file is ${sizeMB.toFixed(1)}MB.`; return;
    }
    this.selectedCoverFile = file;
    const reader = new FileReader();
    reader.onload = (e) => {
      this.selectedCoverUrl = e.target?.result as string;
      this.coverOffsetX = 0; this.coverOffsetY = 0; this.coverZoomLevel = 1;
      setTimeout(() => this.fitCoverImageToCrop(), 50);
      this.cdr.markForCheck();
    };
    reader.onerror = () => {
      this.coverFileError = `Couldn't read "${file.name}". Please try another image.`;
      this.cdr.markForCheck();
    };
    readAsPreviewDataUrl(reader, file);
  }

  fitCoverImageToCrop() {
    const el = this.coverCropImage?.nativeElement;
    if (!el?.naturalWidth) return;
    const scaleX = COVER_CROP_W / (el.offsetWidth  || el.naturalWidth);
    const scaleY = COVER_CROP_H / (el.offsetHeight || el.naturalHeight);
    this.coverInitialScale = Math.max(scaleX, scaleY, 1);
    this.coverZoomLevel    = this.coverInitialScale;
    this.coverOffsetX = 0; this.coverOffsetY = 0;
    this.cdr.markForCheck();
  }

  resetCoverImage() {
    this.selectedCoverUrl = null; this.selectedCoverFile = null;
    this.coverFileError = ''; this.coverZoomLevel = 1; this.coverInitialScale = 1;
    this.coverOffsetX = 0; this.coverOffsetY = 0;
  }

  onCoverDragOver(e: DragEvent)  { e.preventDefault(); this.isCoverDragging = true; }
  onCoverDragLeave(e: DragEvent) { this.isCoverDragging = false; }
  onCoverDrop(e: DragEvent) {
    e.preventDefault(); this.isCoverDragging = false;
    const file = e.dataTransfer?.files[0]; if (file) this.handleCoverFile(file);
  }

  startCoverDrag(e: MouseEvent) {
    this.coverDragging   = true;
    this.coverLastMouseX = e.clientX; this.coverLastMouseY = e.clientY;
    e.preventDefault();
  }

  startCoverDragTouch(e: TouchEvent) {
    this.coverDragging   = true;
    this.coverLastMouseX = e.touches[0].clientX;
    this.coverLastMouseY = e.touches[0].clientY;
  }

  uploadCoverImage() {
    if (!this.selectedCoverUrl || !this.coverExportCanvas || !this.coverCropImage) return;

    const canvas = this.coverExportCanvas.nativeElement;
    const ctx    = canvas.getContext('2d')!;
    const outW   = COVER_CROP_W * 2;
    const outH   = COVER_CROP_H * 2;
    canvas.width  = outW;
    canvas.height = outH;

    const imgEl    = this.coverCropImage.nativeElement;
    const renderedW = imgEl.offsetWidth;
    const renderedH = imgEl.offsetHeight;

    const img = new Image();
    img.onload = () => {
      const imgScaleW = (renderedW / COVER_CROP_W) * outW * this.coverZoomLevel;
      const imgScaleH = (renderedH / COVER_CROP_H) * outH * this.coverZoomLevel;

      const drawX = outW / 2 + (this.coverOffsetX / COVER_CROP_W) * outW - imgScaleW / 2;
      const drawY = outH / 2 + (this.coverOffsetY / COVER_CROP_H) * outH - imgScaleH / 2;

      ctx.drawImage(img, drawX, drawY, imgScaleW, imgScaleH);
      canvas.toBlob(
        (blob) => { if (blob) this.callUploadApi(blob, 'BACKGROUND'); },
        'image/jpeg', 0.92
      );
    };
    img.src = this.selectedCoverUrl;
  }

  // ══════════════════════════════════════════════════════════
  // SHARED API CALL
  // ══════════════════════════════════════════════════════════

  private callUploadApi(blob: Blob, type: 'PROFILE' | 'BACKGROUND') {
    if (type === 'PROFILE') this.isUploading      = true;
    else                    this.isCoverUploading = true;

    const formData = new FormData();
    formData.append('image', blob, type === 'PROFILE' ? 'profile.jpg' : 'cover.jpg');
    formData.append('type', type);

    this.userService.uploadProfileImage(formData).subscribe({
      next: (res: any) => {
        if (type === 'PROFILE') this.isUploading      = false;
        else                    this.isCoverUploading = false;

        if (res.success) {
          if (res.data) this.authState.updateUser(res.data);
          if (type === 'PROFILE') this.closeProfileImageModal();
          else                    this.closeCoverImageModal();

          const userName = this.route.snapshot.paramMap.get('userName');
          this.getProfile(userName);
        } else {
          console.error('Upload failed:', res);
        }
      },
      error: (err) => {
        if (type === 'PROFILE') this.isUploading      = false;
        else                    this.isCoverUploading = false;
        console.error('Upload error:', err);
      }
    });
  }

  // ══════════════════════════════════════════════════════════
  // MOUSE / TOUCH LISTENERS
  // ══════════════════════════════════════════════════════════

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(e: MouseEvent) {
    if (this.dragging) {
      this.offsetX += e.clientX - this.lastMouseX;
      this.offsetY += e.clientY - this.lastMouseY;
      this.lastMouseX = e.clientX; this.lastMouseY = e.clientY;
    }
    if (this.coverDragging) {
      this.coverOffsetX += e.clientX - this.coverLastMouseX;
      this.coverOffsetY += e.clientY - this.coverLastMouseY;
      this.coverLastMouseX = e.clientX; this.coverLastMouseY = e.clientY;
    }
  }

  startDrag(e: MouseEvent) {
    this.dragging   = true;
    this.lastMouseX = e.clientX; this.lastMouseY = e.clientY;
    e.preventDefault();
  }

  startDragTouch(e: TouchEvent) {
    this.dragging   = true;
    this.lastMouseX = e.touches[0].clientX;
    this.lastMouseY = e.touches[0].clientY;
  }

  @HostListener('document:mouseup')
  onMouseUp() { this.dragging = false; this.coverDragging = false; }

  @HostListener('document:touchmove', ['$event'])
  onTouchMove(e: TouchEvent) {
    if (this.dragging) {
      this.offsetX += e.touches[0].clientX - this.lastMouseX;
      this.offsetY += e.touches[0].clientY - this.lastMouseY;
      this.lastMouseX = e.touches[0].clientX; this.lastMouseY = e.touches[0].clientY;
    }
    if (this.coverDragging) {
      this.coverOffsetX += e.touches[0].clientX - this.coverLastMouseX;
      this.coverOffsetY += e.touches[0].clientY - this.coverLastMouseY;
      this.coverLastMouseX = e.touches[0].clientX; this.coverLastMouseY = e.touches[0].clientY;
    }
  }

  @HostListener('document:touchend')
  onTouchEnd() { this.dragging = false; this.coverDragging = false; }

  // ══════════════════════════════════════════════════════════
  // FRIEND / FOLLOW ACTIONS
  // ══════════════════════════════════════════════════════════

  friendButtonTitle(status?: FriendshipStatus): string {
    const map: Record<FriendshipStatus, string> = {
      NONE:             'Send friend request',
      PENDING_SENT:     'Cancel friend request',
      PENDING_RECEIVED: 'Accept friend request',
      FRIENDS:          'Unfriend',
      BLOCKED:          'Blocked',
    };
    return status ? map[status] : '';
  }

  public follow(event: MouseEvent): void {
    event.stopPropagation();
    // Keep the Followers count in step with the button (it used to stay stale).
    this.userInfo.isFollowed = true;
    this.userInfo.followers = (this.userInfo.followers ?? 0) + 1;
    this.cdr.detectChanges();
    this.relationshipService.follow(this.userInfo.id).subscribe({
      error: () => {
        this.userInfo.isFollowed = false;
        this.userInfo.followers = Math.max(0, (this.userInfo.followers ?? 1) - 1);
        this.cdr.detectChanges();
      },
    });
  }

  public unfollow(event: MouseEvent): void {
    event.stopPropagation();
    this.userInfo.isFollowed = false;
    this.userInfo.followers = Math.max(0, (this.userInfo.followers ?? 1) - 1);
    this.cdr.detectChanges();
    this.relationshipService.unfollow(this.userInfo.id).subscribe({
      error: () => {
        this.userInfo.isFollowed = true;
        this.userInfo.followers = (this.userInfo.followers ?? 0) + 1;
        this.cdr.detectChanges();
      },
    });
  }

  onFriendButtonClick(event: MouseEvent): void {
    event.stopPropagation();
    switch (this.userInfo.friendship.status) {
      case 'NONE':             this.sendFriendRequest(event);   break;
      case 'PENDING_SENT':     this.cancelFriendRequest(event); break;
      case 'PENDING_RECEIVED': this.acceptFriendRequest(event); break;
      case 'FRIENDS':          this.promptUnfriend(event);      break;
      case 'BLOCKED':                                           break;
    }
  }

  private sendFriendRequest(event: MouseEvent): void {
    event.stopPropagation();
    if (this.userInfo.friendship.status !== 'NONE') return;
    this.userInfo.friendship.status = 'PENDING_SENT'; this.cdr.detectChanges();
    this.friendRequestService.send(this.userInfo.id).subscribe({
      next: (res: any) => { this.userInfo.friendship.requestId = res?.data?._id ?? null; this.cdr.detectChanges(); },
      error: (err) => {
        const status = err?.error?.statusCode ?? err?.status;
        if (status !== 409) this.userInfo.friendship.status = 'NONE';
        const cooldownHours = this.friendRequestService.parseCooldownError(err);
        if (cooldownHours !== null) {
          this.friendLimitHours = cooldownHours;
          this.showFriendLimitPopup = true;
        }
        this.cdr.detectChanges();
      },
    });
  }

  showFriendLimitPopup = false;
  friendLimitHours: number | null = null;

  private cancelFriendRequest(event: MouseEvent): void {
    event.stopPropagation();
    if (!this.userInfo.friendship.status) return;
    const prevStatus    = this.userInfo.friendship.status;
    const prevRequestId = this.userInfo.friendship.requestId;
    this.userInfo.friendship.status = 'NONE'; this.cdr.detectChanges();
    this.friendRequestService.cancel(this.userInfo.friendship.requestId, this.userInfo.id).subscribe({
      error: () => { this.userInfo.friendship.status = prevStatus; this.userInfo.friendship.requestId = prevRequestId; this.cdr.detectChanges(); },
    });
  }

  showUnfriendConfirm = false;
  private pendingUnfriendAction: (() => void) | null = null;

  private promptUnfriend(event: MouseEvent): void {
    event.stopPropagation();
    this.pendingUnfriendAction = () => this.unfriend(event);
    this.showUnfriendConfirm = true;
  }

  confirmUnfriend(): void {
    this.showUnfriendConfirm = false;
    this.pendingUnfriendAction?.();
    this.pendingUnfriendAction = null;
  }

  cancelUnfriendConfirm(): void {
    this.showUnfriendConfirm = false;
    this.pendingUnfriendAction = null;
  }

  private unfriend(event: MouseEvent): void {
    event.stopPropagation();
    const prevStatus    = this.userInfo.friendship.status;
    const prevRequestId = this.userInfo.friendship.requestId;
    this.userInfo.friendship.status    = 'NONE';
    this.userInfo.friendship.requestId = null; this.cdr.detectChanges();
    this.friendRequestService.unfriend(this.userInfo.id).subscribe({
      error: () => { this.userInfo.friendship.status = prevStatus; this.userInfo.friendship.requestId = prevRequestId; this.cdr.detectChanges(); },
    });
  }

  public acceptFriendRequest(event: MouseEvent): void {
    event.stopPropagation();
    const prevStatus = this.userInfo.friendship.status;
    this.userInfo.friendship.status = 'FRIENDS'; this.cdr.detectChanges();
    this.friendRequestService.accept(this.userInfo.friendship.requestId, this.userInfo.id).subscribe({
      error: () => { this.userInfo.friendship.status = prevStatus; this.cdr.detectChanges(); },
    });
  }

  public rejectFriendRequest(event: MouseEvent): void {
    event.stopPropagation();
    const prevStatus    = this.userInfo.friendship.status;
    const prevRequestId = this.userInfo.friendship.requestId;
    this.userInfo.friendship.status    = 'NONE';
    this.userInfo.friendship.requestId = null; this.cdr.detectChanges();
    this.friendRequestService.decline(this.userInfo.friendship.requestId ?? prevRequestId, this.userInfo.id).subscribe({
      error: () => { this.userInfo.friendship.status = prevStatus; this.userInfo.friendship.requestId = prevRequestId; this.cdr.detectChanges(); },
    });
  }

  // ══════════════════════════════════════════════════════════
  // BLOCK / UNBLOCK
  // ══════════════════════════════════════════════════════════

  isMoreMenuOpen = false;

  toggleMoreMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.isMoreMenuOpen = !this.isMoreMenuOpen;
  }

  closeMoreMenu(): void {
    this.isMoreMenuOpen = false;
  }

  showBlockConfirm = false;

  promptBlockUser(event?: MouseEvent): void {
    event?.stopPropagation();
    this.isMoreMenuOpen = false;
    this.showBlockConfirm = true;
  }

  cancelBlockConfirm(): void {
    this.showBlockConfirm = false;
  }

  confirmBlockUser(): void {
    this.showBlockConfirm = false;
    if (!this.userInfo?.id) return;

    const prevInfo = this.userInfo;
    // Optimistically switch to the "blocked by me" state — mirrors what a
    // fresh profile fetch would return once the block takes effect.
    this.userInfo = {
      ...this.userInfo,
      isBlockedByMe: true,
      isFollowed: false,
      friendship: { status: 'BLOCKED' },
    };
    this.cdr.detectChanges();

    this.friendRequestService.block(this.userInfo.id).subscribe({
      next: () => this.friendRequestService.triggerRefresh(),
      error: () => { this.userInfo = prevInfo; this.cdr.detectChanges(); },
    });
  }

  unblockUser(event?: MouseEvent): void {
    event?.stopPropagation();
    if (!this.userInfo?.id) return;

    const targetId = this.userInfo.id;
    this.friendRequestService.unblock(targetId).subscribe({
      next: () => {
        this.friendRequestService.triggerRefresh();
        // Re-fetch so the viewer gets the full profile back now the block is lifted.
        const userName = this.route.snapshot.paramMap.get('userName');
        this.getProfile(userName);
      },
      error: (err) => console.error('Unblock failed:', err),
    });
  }
}
